import newsGeneric from '../assets/images/news-generic.svg';

export interface NewsArticle {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  category: string;
  image: string;
  readTime: string;
  tags: string[];
  featured?: boolean;
}

export interface NewsArticlePreview {
  id: number;
  title: string;
  date: string;
  excerpt: string;
  image: string;
}

export const newsArticles: NewsArticle[] = [
  {
    id: 1,
    title: 'DEBUT ALBUM COMING LATER THIS YEAR',
    excerpt:
      'The Kults are putting the finishing touches on their first album — recorded between Basel and Texas, and set to arrive before the year is out.',
    content: `
        <p>After months of writing and recording across two continents, The Kults are nearing completion on their debut album. Built in sessions between Basel, Switzerland and Texas, USA, the record reflects everything we've been building since day one — raw rock energy, modern pop sensibility, and a sound that feels both timeless and right now.</p>
        
        <p>The album brings together songs shaped on both sides of the Atlantic. Che, Stewart, and Caleb have been refining every track, from the driving guitars to the layered vocals and production details that give each song its own identity. It's the most honest portrait of this band we've ever put to tape.</p>
        
        <p>What to expect from the debut:</p>
        
        <ul>
          <li>A full-length album titled <strong>THE KULTS</strong></li>
          <li>12 tracks blending contemporary rock/pop with a retro soul</li>
          <li>Singles including "Collide," "Nick of Time," and "Typewriter Boxer"</li>
          <li>Release planned for later this year — full date and pre-order details coming soon</li>
        </ul>
        
        <p>We've been deliberate about getting this right. Recording between Basel and Texas hasn't just been a logistical detail — it's shaped the music. European grit meets American swagger, and we can't wait for you to hear it.</p>
        
        <p>Stay tuned for the official release date, tracklist reveal, and pre-order information. Sign up for our newsletter to be first in line when the debut drops.</p>
      `,
    date: 'August 1, 2026',
    author: 'The Kults Team',
    category: 'Album Release',
    image: newsGeneric,
    readTime: '4 min read',
    tags: ['Debut Album', 'THE KULTS', 'New Music', '2026'],
    featured: true,
  },
  {
    id: 2,
    title: 'NICK OF TIME — OFFICIAL MUSIC VIDEO COMING IN AUGUST',
    excerpt:
      'The Kults will release their first official music video for "Nick of Time" this August — a preview of what\'s coming on the debut album.',
    content: `
        <p>We're getting ready to share something we've been building toward from the start: the official music video for "Nick of Time" — our first video, arriving this August.</p>
        
        <p>"Nick of Time" is one of the songs that set the tone for everything that followed on our debut album. Written and produced by the band, it captures the urgency and attitude at the heart of The Kults — tight hooks, live-wire guitars, and a chorus built for turning up the volume.</p>
        
        <p>The video was shot to match the energy of the track — bold visuals, cinematic framing, and the same artistic edge Stewart brings to the band's identity off stage. It's our introduction to the world in moving pictures, and we can't wait to finally put it out there.</p>
        
        <p>What to expect when it drops in August:</p>
        
        <ul>
          <li>Featured on the homepage under Featured Videos</li>
          <li>Release across our social channels and the Music page</li>
          <li>A first look at the sound and vision behind the debut album</li>
        </ul>
        
        <p>"Nick of Time" is just the beginning. Our debut album is on the way later this year, and this video will be your first moving-image look at what's coming. Thanks for being here from the start — more music, more visuals, and the full record are right around the corner.</p>
        
        <p>Mark your calendars for August and stay connected for the exact premiere date and tour news ahead.</p>
      `,
    date: 'August 15, 2026',
    author: 'The Kults Team',
    category: 'Music Video',
    image: newsGeneric,
    readTime: '3 min read',
    tags: ['Nick of Time', 'Music Video', 'Single', 'Debut'],
  },
  {
    id: 3,
    title: 'COLLIDE — OUR FIRST SINGLE, OUT NOW ON ALL PLATFORMS',
    excerpt:
      '"Collide" is live on Spotify, Apple Music, and every major streaming service — The Kults\' first single is here.',
    content: `
        <p>Today we're releasing "Collide" — our first official single, and the first taste of what's coming on our debut album. It's live now on every major streaming platform.</p>
        
        <p>"Collide" is the song that started it all for us as a recording act. Built on a driving rhythm and a melody that won't let go, it captures the collision of influences that define The Kults — vintage rock swagger, modern pop hooks, and the transatlantic chemistry we built between Basel and Texas.</p>
        
        <p>Listen wherever you stream music:</p>
        
        <ul>
          <li>Spotify</li>
          <li>Apple Music</li>
          <li>Amazon Music</li>
          <li>YouTube Music</li>
          <li>Deezer</li>
          <li>And all other major digital platforms worldwide</li>
        </ul>
        
        <p>Whether you're adding it to a playlist, sharing it with a friend, or playing it loud on the drive home — we made this one for you. "Collide" is just the opening chapter. More singles, our first music video, and the full debut album are all on the way.</p>
        
        <p>Stream "Collide" now, follow The Kults on your platform of choice, and stay tuned for what's next.</p>
      `,
    date: 'September 1, 2026',
    author: 'The Kults Team',
    category: 'Single Release',
    image: newsGeneric,
    readTime: '3 min read',
    tags: ['Collide', 'Single', 'Streaming', 'Debut'],
  },
];

export const parseArticleDate = (dateStr: string): number =>
  new Date(dateStr).getTime();

export const sortNewsByDateDesc = <T extends { date: string }>(
  articles: T[]
): T[] =>
  [...articles].sort(
    (a, b) => parseArticleDate(b.date) - parseArticleDate(a.date)
  );

export const toNewsPreview = (article: NewsArticle): NewsArticlePreview => ({
  id: article.id,
  title: article.title,
  date: article.date,
  excerpt: article.excerpt,
  image: article.image,
});

export const getSortedNewsArticles = (): NewsArticle[] =>
  sortNewsByDateDesc(newsArticles);

export const getNewsArticleById = (id: number): NewsArticle | undefined =>
  newsArticles.find((article) => article.id === id);

export const getLatestNewsPreviews = (
  maxArticles?: number
): NewsArticlePreview[] => {
  const previews = getSortedNewsArticles().map(toNewsPreview);
  return maxArticles ? previews.slice(0, maxArticles) : previews;
};
